import React, { useEffect, useState, useMemo, useCallback } from "react";
import { http, currency } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Search, MessageCircle, Mail, FileDown, CheckSquare, Square, Users } from "lucide-react";
import { toast } from "sonner";

// Customer database: aggregates buyers from historical orders and lets admin
// broadcast promotional messages via WhatsApp/email or export the list.
export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [message, setMessage] = useState(
    "¡Hola {nombre}! Somos de Printec del Norte. Tenemos nuevos productos que creemos te van a encantar. Escríbenos para ver los diseños y precios especiales para clientes 🎉"
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/customers");
      setCustomers(data || []);
    } catch {
      toast.error("No se pudieron cargar los clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return customers;
    return customers.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(needle) ||
        (c.email || "").toLowerCase().includes(needle) ||
        (c.phone || "").toLowerCase().includes(needle)
    );
  }, [customers, query]);

  const toggleOne = (key) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return s;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.key)));
    }
  };

  const stats = useMemo(() => ({
    total: customers.length,
    withPhone: customers.filter((c) => c.phone).length,
    withEmail: customers.filter((c) => c.email).length,
    totalRevenue: customers.reduce((a, c) => a + Number(c.total_spent || 0), 0),
  }), [customers]);

  const buildMessage = (c) => {
    const firstName = (c.name || "").split(" ")[0] || "cliente";
    return message.replace(/\{nombre\}/g, firstName);
  };

  const sanitizePhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    // Add MX country code if 10 digits (national) and not already prefixed
    if (digits.length === 10) return `52${digits}`;
    return digits;
  };

  const waLink = (c) => {
    const phone = sanitizePhone(c.phone);
    if (!phone) return null;
    return `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(c))}`;
  };

  const mailtoLink = (c) => {
    if (!c.email) return null;
    const subject = "Nuevos productos · Printec del Norte";
    return `mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMessage(c))}`;
  };

  const sendSelectedWhatsApp = () => {
    const list = filtered.filter((c) => selected.has(c.key) && c.phone);
    if (list.length === 0) {
      toast.error("Selecciona clientes con teléfono");
      return;
    }
    // Open up to 5 tabs to avoid overwhelming the browser; otherwise instruct the admin.
    const cap = Math.min(list.length, 5);
    for (let i = 0; i < cap; i++) {
      const url = waLink(list[i]);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    }
    if (list.length > cap) {
      toast.info(`Abrí ${cap} de ${list.length}. Envía y regresa para abrir los siguientes.`);
    } else {
      toast.success(`Abrí ${cap} conversación${cap === 1 ? "" : "es"} de WhatsApp`);
    }
  };

  const exportCSV = () => {
    const list = selected.size ? filtered.filter((c) => selected.has(c.key)) : filtered;
    if (list.length === 0) return toast.error("No hay clientes para exportar");
    const rows = [
      ["Nombre", "Email", "Teléfono", "Ciudad", "Estado", "Pedidos", "Total comprado", "Último pedido"],
      ...list.map((c) => [
        c.name || "",
        c.email || "",
        c.phone || "",
        c.city || "",
        c.state || "",
        c.order_count || 0,
        c.total_spent || 0,
        c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("es-MX", { timeZone: "America/Monterrey" }) : "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => {
        const s = String(cell).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `clientes_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyPhonesToClipboard = () => {
    const list = filtered.filter((c) => selected.has(c.key) && c.phone);
    if (list.length === 0) return toast.error("Selecciona clientes con teléfono");
    const text = list.map((c) => sanitizePhone(c.phone)).join(", ");
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${list.length} teléfono(s) copiado(s) al portapapeles`),
      () => toast.error("No se pudo copiar")
    );
  };

  return (
    <div className="space-y-5" data-testid="admin-customers">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5" />
        <h2 className="font-display text-2xl">Base de datos de clientes</h2>
        <div className="text-xs font-mono uppercase text-neutral-500 ml-auto">
          Enviar publicidad a clientes existentes
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Clientes únicos", value: stats.total },
          { label: "Con teléfono", value: stats.withPhone },
          { label: "Con email", value: stats.withEmail },
          { label: "Ventas históricas", value: currency(stats.totalRevenue) },
        ].map((k) => (
          <div key={k.label} className="border border-neutral-200 p-3">
            <div className="text-[10px] font-mono uppercase text-neutral-500">{k.label}</div>
            <div className="font-display text-2xl mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Message template + broadcast controls */}
      <div className="border border-neutral-200 p-4 space-y-2">
        <div className="text-xs font-mono uppercase text-neutral-500">Mensaje de publicidad</div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-none resize-y whitespace-pre-wrap focus:border-black focus:outline-none"
          placeholder="Usa {nombre} para insertar el nombre del cliente…"
          data-testid="admin-customers-message"
        />
        <div className="text-[10px] text-neutral-500 italic">
          Tip: escribe <code>{"{nombre}"}</code> para insertar automáticamente el nombre del cliente.
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            onClick={sendSelectedWhatsApp}
            disabled={selected.size === 0}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            data-testid="admin-customers-send-wa"
          >
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
            Enviar por WhatsApp ({selected.size})
          </Button>
          <Button
            onClick={copyPhonesToClipboard}
            disabled={selected.size === 0}
            variant="outline"
            className="rounded-none text-xs"
            data-testid="admin-customers-copy-phones"
          >
            Copiar teléfonos
          </Button>
          <Button
            onClick={exportCSV}
            variant="outline"
            className="rounded-none text-xs"
            data-testid="admin-customers-export"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
            Exportar CSV ({selected.size || filtered.length})
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, email o teléfono…"
          className="rounded-none h-10 pl-9 text-sm"
          data-testid="admin-customers-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-neutral-200 p-8 text-center text-sm text-neutral-500 italic">
          No hay clientes que coincidan con la búsqueda.
        </div>
      ) : (
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left">
              <tr>
                <th className="p-3 w-10">
                  <button onClick={toggleAll} data-testid="admin-customers-toggle-all" title="Seleccionar todos">
                    {selected.size === filtered.length && filtered.length > 0
                      ? <CheckSquare className="w-4 h-4" />
                      : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Cliente</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Contacto</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-right">Pedidos</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-right">Total comprado</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Último pedido</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-right">Enviar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const wa = waLink(c);
                const mail = mailtoLink(c);
                const last = c.last_order_at
                  ? new Date(c.last_order_at).toLocaleDateString("es-MX", { timeZone: "America/Monterrey" })
                  : "—";
                const active = selected.has(c.key);
                return (
                  <tr
                    key={c.key}
                    className={`border-b border-neutral-100 ${active ? "bg-emerald-50/40" : "hover:bg-neutral-50"}`}
                    data-testid={`admin-customer-row-${c.key}`}
                  >
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleOne(c.key)}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                        data-testid={`admin-customer-check-${c.key}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{c.name || <span className="italic text-neutral-400">Sin nombre</span>}</div>
                      <div className="text-[10px] text-neutral-500 font-mono">{c.city}{c.city && c.state ? ", " : ""}{c.state}</div>
                    </td>
                    <td className="p-3 text-xs">
                      {c.email && <div className="text-neutral-600 truncate max-w-[220px]" title={c.email}>{c.email}</div>}
                      {c.phone && <div className="text-neutral-600 font-mono">📱 {c.phone}</div>}
                      {!c.email && !c.phone && <div className="text-neutral-400 italic">—</div>}
                    </td>
                    <td className="p-3 text-right">
                      <div className="font-mono">{c.order_count}</div>
                      {c.cancelled_count > 0 && (
                        <div className="text-[9px] text-red-600 font-mono">({c.cancelled_count} cancel.)</div>
                      )}
                    </td>
                    <td className="p-3 text-right font-medium">{currency(c.total_spent)}</td>
                    <td className="p-3 text-xs text-neutral-600 font-mono">{last}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        {wa && (
                          <a
                            href={wa}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                            title="Enviar WhatsApp"
                            data-testid={`admin-customer-wa-${c.key}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {mail && (
                          <a
                            href={mail}
                            className="p-1.5 border border-blue-500 text-blue-700 hover:bg-blue-50"
                            title="Enviar Email"
                            data-testid={`admin-customer-mail-${c.key}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
